/**
 * UploadRouteDialog — extraído de Dashboard.js (refactor Fev/2026).
 *
 * Modal para admin carregar uma nova rota (GPX/KML) + metadados,
 * capa, logos, fotos, visibilidade e grupos.
 *
 * Props:
 *   - open, onOpenChange        (estado do Dialog)
 *   - data, setData             (estado do formulário, controlado pelo pai)
 *   - uploading                 (loading do submit)
 *   - onSubmit                  (handler async - recebe o form event)
 *   - availableGroups           (lista de grupos p/ visibilidade)
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload } from 'lucide-react';

const CATEGORIES = [
  { id: 'suv-4x4', label: '🚙 SUV 4x4', kind: 'type' },
  { id: '4x4-reductoras', label: '🚜 4x4 Redutoras', kind: 'type' },
  { id: 'car', label: '🚗 Carro', kind: 'badge' },
  { id: 'moto', label: '🏍️ Mota', kind: 'badge' },
];

export const UploadRouteDialog = ({ open, onOpenChange, data, setData, uploading, onSubmit, availableGroups, availableCompanies = [], availableMunicipalities = [], turnsStatus = null }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="w-full mt-4 rounded-md" data-testid="upload-route-button">
          <Upload className="mr-2 h-4 w-4" />
          Carregar Rota
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle>Carregar Nova Rota</DialogTitle>
          <DialogDescription>Envie um ficheiro GPX ou KML</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="route-name">Nome da Rota *</Label>
            <Input
              id="route-name"
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              required
              data-testid="route-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="route-description">Descrição (opcional)</Label>
            <Textarea
              id="route-description"
              value={data.description}
              onChange={(e) => setData({ ...data, description: e.target.value })}
              data-testid="route-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="route-difficulty">Dificuldade</Label>
              <Select
                value={data.difficulty}
                onValueChange={(value) => setData({ ...data, difficulty: value })}
              >
                <SelectTrigger id="route-difficulty" data-testid="route-difficulty-select">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Fácil</SelectItem>
                  <SelectItem value="moderate">Moderada</SelectItem>
                  <SelectItem value="hard">Difícil</SelectItem>
                  <SelectItem value="expert">Especialista</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Categoria (marca as que se aplicam)</Label>
              <div className="grid grid-cols-2 gap-1 border border-slate-200 rounded-md p-2 bg-slate-50">
                {CATEGORIES.map((opt) => {
                  const checked = opt.kind === 'type'
                    ? data.route_type === opt.id
                    : (data.vehicle_badges || []).includes(opt.id);
                  return (
                    <label
                      key={opt.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition ${
                        checked ? 'bg-white ring-1 ring-primary/40' : 'hover:bg-white/60'
                      }`}
                      data-testid={`upload-cat-opt-${opt.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          if (opt.kind === 'type') {
                            setData({
                              ...data,
                              route_type: v ? opt.id : (data.route_type === opt.id ? '' : data.route_type),
                            });
                          } else {
                            const cur = new Set(data.vehicle_badges || []);
                            if (v) cur.add(opt.id); else cur.delete(opt.id);
                            setData({ ...data, vehicle_badges: Array.from(cur) });
                          }
                        }}
                      />
                      <span>{opt.label}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500">SUV 4x4 e 4x4 Redutoras são exclusivos entre si.</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="route-file">Ficheiro GPX/KML *</Label>
            <Input
              id="route-file"
              type="file"
              accept=".gpx,.kml"
              onChange={(e) => setData({ ...data, file: e.target.files[0] })}
              required
              data-testid="route-file-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="route-cover">Foto de capa (opcional)</Label>
            <Input
              id="route-cover"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setData({ ...data, coverImage: e.target.files[0] })}
              data-testid="route-cover-input"
            />
            {data.coverImage && (
              <p className="text-xs text-slate-500">📷 {data.coverImage.name}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="route-logos">Logos (patrocinadores/clubes — máx. 5)</Label>
            <Input
              id="route-logos"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/svg+xml"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []).slice(0, 5);
                setData({ ...data, logos: files });
              }}
              data-testid="route-logos-input"
            />
            {Array.isArray(data.logos) && data.logos.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {data.logos.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="relative"
                    data-testid={`upload-logo-preview-${i}`}
                  >
                    <img
                      src={URL.createObjectURL(f)}
                      alt=""
                      className="h-12 w-12 rounded-lg object-cover border border-slate-200"
                      onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                    />
                    <button
                      type="button"
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white text-xs font-bold leading-none flex items-center justify-center hover:bg-red-700"
                      onClick={() => {
                        const next = data.logos.filter((_, idx) => idx !== i);
                        setData({ ...data, logos: next });
                      }}
                      data-testid={`upload-logo-remove-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <span className="text-[10px] text-slate-500 self-end">
                  {data.logos.length}/5
                </span>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="route-photos">Galeria de fotos (paisagens, obstáculos — máx. 10)</Label>
            <Input
              id="route-photos"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []).slice(0, 10);
                setData({ ...data, photos: files });
              }}
              data-testid="route-photos-input"
            />
            {Array.isArray(data.photos) && data.photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {data.photos.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="relative"
                    data-testid={`upload-photo-preview-${i}`}
                  >
                    <img
                      src={URL.createObjectURL(f)}
                      alt=""
                      className="h-16 w-24 rounded-lg object-cover border border-slate-200"
                      onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                    />
                    <button
                      type="button"
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white text-xs font-bold leading-none flex items-center justify-center hover:bg-red-700"
                      onClick={() => {
                        const next = data.photos.filter((_, idx) => idx !== i);
                        setData({ ...data, photos: next });
                      }}
                      data-testid={`upload-photo-remove-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <span className="text-[10px] text-slate-500 self-end">
                  {data.photos.length}/10
                </span>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="route-region">Zona / Região</Label>
            <Input
              id="route-region"
              placeholder="Ex: Serra da Estrela, Gerês..."
              value={data.region}
              onChange={(e) => setData({ ...data, region: e.target.value })}
              data-testid="route-region-input"
            />
          </div>
          {/* Fev/2026 (Ricardo) — Admin pode associar logo a empresa + município.
             Backend aceita `company_id` e `municipality_ids` (CSV) no upload. */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="route-company">🏢 Empresa (opcional)</Label>
              <Select
                value={data.company_id || 'none'}
                onValueChange={(v) => setData({ ...data, company_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger id="route-company" data-testid="upload-company-select">
                  <SelectValue placeholder="Nenhuma" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nenhuma —</SelectItem>
                  {availableCompanies.map((c) => (
                    <SelectItem key={c.company_id} value={c.company_id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="route-muni">🏛️ Município (opcional)</Label>
              <Select
                value={(data.municipality_ids && data.municipality_ids[0]) || 'none'}
                onValueChange={(v) => setData({ ...data, municipality_ids: v === 'none' ? [] : [v] })}
              >
                <SelectTrigger id="route-muni" data-testid="upload-muni-select">
                  <SelectValue placeholder="Nenhum" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nenhum —</SelectItem>
                  {availableMunicipalities.map((m) => (
                    <SelectItem key={m.municipality_id} value={m.municipality_id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="route-tags">Etiquetas (separadas por vírgula, máx. 10)</Label>
            <Input
              id="route-tags"
              placeholder="serra, inverno, técnico, panorâmico"
              value={data.tags}
              onChange={(e) => setData({ ...data, tags: e.target.value })}
              data-testid="route-tags-input"
            />
            {data.tags && (
              <div className="flex flex-wrap gap-1 mt-1">
                {data.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean).slice(0, 10).map((tag) => (
                  <span key={tag} className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">#{tag}</span>
                ))}
              </div>
            )}
          </div>
          {/* Visibility — admin escolhe explicitamente PÚBLICA ou PRIVADA.
             Se escolheu empresa acima, o backend força `is_private=true`
             independentemente deste toggle → desabilitamos para não confundir. */}
          {data.company_id ? (
            <div className="space-y-2 border-t pt-3">
              <Label className="flex items-center gap-2">
                🔒 Visibilidade da rota
                <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">PRIVADA</span>
              </Label>
              <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 leading-relaxed" data-testid="upload-company-private-badge">
                🏢 Associada à empresa seleccionada → <strong>privada automaticamente</strong>, só membros com o <code className="bg-amber-100 px-1 rounded">company_id</code> correspondente conseguem ver e navegar.
              </div>
            </div>
          ) : (
          <div className="space-y-2 border-t pt-3">
            <Label className="flex items-center gap-2">
              🔒 Visibilidade da rota
              {data.is_private ? (
                <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">PRIVADA</span>
              ) : (
                <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">PÚBLICA</span>
              )}
            </Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setData({ ...data, is_private: false })}
                className={`flex-1 px-3 py-2 rounded-md text-xs font-bold transition border-2 ${
                  !data.is_private
                    ? 'bg-emerald-600 text-white border-emerald-700'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'
                }`}
                data-testid="upload-visibility-public-btn"
              >
                🌍 PÚBLICA<br /><span className="text-[10px] font-normal opacity-80">Qualquer utilizador vê</span>
              </button>
              <button
                type="button"
                onClick={() => setData({ ...data, is_private: true })}
                className={`flex-1 px-3 py-2 rounded-md text-xs font-bold transition border-2 ${
                  data.is_private
                    ? 'bg-amber-600 text-white border-amber-700'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-amber-400'
                }`}
                data-testid="upload-visibility-private-btn"
              >
                🔒 PRIVADA<br /><span className="text-[10px] font-normal opacity-80">Só membros dos grupos abaixo</span>
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Mesmo PÚBLICA, podes associar grupos (apenas para organização). Em PRIVADA, só membros dos grupos selecionados conseguem ver e navegar a rota.
            </p>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1">
              {availableGroups.length === 0 && (
                <span className="text-xs text-slate-400">Sem grupos criados ainda.</span>
              )}
              {availableGroups.map((g) => {
                const selected = (data.group_ids || []).includes(g.group_id);
                return (
                  <button
                    key={g.group_id}
                    type="button"
                    onClick={() => {
                      const cur = new Set(data.group_ids || []);
                      if (cur.has(g.group_id)) cur.delete(g.group_id); else cur.add(g.group_id);
                      setData({ ...data, group_ids: Array.from(cur) });
                    }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition ${
                      selected
                        ? 'bg-slate-900 text-white border-2 border-slate-900'
                        : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`upload-group-pick-${g.group_id}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: g.color }} />
                    {g.name}
                    <span className="text-[10px] opacity-70">({g.member_count})</span>
                  </button>
                );
              })}
            </div>
          </div>
          )}
          <label className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3 cursor-pointer hover:bg-muted/50 transition-colors">
            <input
              type="checkbox"
              checked={!!data.draft}
              onChange={(e) => setData({ ...data, draft: e.target.checked })}
              className="mt-1"
              data-testid="upload-draft-checkbox"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold text-foreground">Guardar como rascunho</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Não envia notificação push aos utilizadores. Ideal para preparar rotas em batch antes de publicar.
              </div>
            </div>
          </label>
          <Button type="submit" className="w-full" disabled={uploading || (turnsStatus && turnsStatus.state === 'computing')} data-testid="upload-submit-button">
            {uploading ? 'A carregar...' : data.draft ? 'Guardar rascunho' : 'Publicar e notificar'}
          </Button>
          {/* Fev/2026 (Ricardo) — status real do cálculo BRouter após upload */}
          {turnsStatus && (
            <div
              className={`rounded-lg border-2 p-4 ${
                turnsStatus.state === 'computing' ? 'border-blue-300 bg-blue-50' :
                turnsStatus.state === 'done' ? 'border-emerald-300 bg-emerald-50' :
                'border-rose-300 bg-rose-50'
              }`}
              data-testid="turns-status-panel"
            >
              {turnsStatus.state === 'computing' && (
                <div className="flex items-center gap-3">
                  <div className="h-5 w-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-blue-900">A calcular curvas via BRouter...</div>
                    <div className="text-xs text-blue-700 mt-0.5">Perfil: trekking · daemon local (até 45s)</div>
                  </div>
                </div>
              )}
              {turnsStatus.state === 'done' && (
                <div className="space-y-1">
                  <div className="text-sm font-bold text-emerald-900 flex items-center gap-2">
                    ✓ Rota pronta para navegação
                  </div>
                  <div className="text-xs text-emerald-800 grid grid-cols-2 gap-1 mt-2">
                    <div><strong>{turnsStatus.count}</strong> curvas detectadas</div>
                    {turnsStatus.dist_m != null && <div><strong>{(turnsStatus.dist_m / 1000).toFixed(1)} km</strong> de rota</div>}
                    {turnsStatus.asc_m != null && <div>Subida: <strong>{Math.round(turnsStatus.asc_m)}m</strong></div>}
                    {turnsStatus.profile && <div>Perfil: <strong>{turnsStatus.profile}</strong></div>}
                  </div>
                </div>
              )}
              {turnsStatus.state === 'failed' && (
                <div className="space-y-2">
                  <div className="text-sm font-bold text-rose-900 flex items-center gap-2">
                    ✗ Falha no cálculo de curvas
                  </div>
                  <div className="text-xs text-rose-800 bg-white rounded p-2 font-mono break-all">
                    {turnsStatus.error || 'Erro desconhecido'}
                  </div>
                  <div className="text-xs text-rose-700">
                    A rota foi gravada mas o HUD/TTS do mobile vai ficar sem instruções. Vai a <strong>Erros</strong> ou executa o script <code>scripts/recompute_route_turns.py &lt;route_id&gt;</code> no VPS para re-tentar.
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadRouteDialog;
